import {
  PROJECT_STATUSES,
  PROJECT_STATUS_WORKFLOW,
  TEST_TYPES_LABELS,
} from 'Constants';
import { format, parseISO } from 'date-fns';
import React from 'react';

import {
  Biotech as BiotechIcon,
  CalendarMonth as CalendarMonthIcon,
  Coronavirus as CoronavirusIcon,
  CropSquare as CropSquareIcon,
  Edit as EditIcon,
  Height as HeightIcon,
  HelpOutline as HelpOutlineIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ImageOutlined as ImageOutlinedIcon,
  ViewInAr as ViewInArIcon,
} from '@mui/icons-material';
import {
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { ScenariosTableSingleTest } from 'Components/Scenarios';
import { CreateScenarioButton } from 'Components/Scenarios/Buttons';
// import { CreateScenarioButton } from 'Components/Scenarios/Buttons';
import { TestScenariosContextProvider } from 'Components/Scenarios/context';
import Floorplan from 'Components/SingleTest/Elements/Floorplan';
import { useTestContext } from 'Components/SingleTest/context';

import { isSafetracesAdmin } from 'Config/roles';

export const TestCharacteristicItem = ({
  icon = null,
  label,
  value = '',
  action = null,
  explanationMsg = '',
  variant = 'primary',
  sx,
}) => {
  return (
    <Stack direction="row" sx={{ ...sx }}>
      {icon}
      <Typography
        sx={{
          ml: icon ? 1 : 0,
          fontWeight: variant === 'secondary' ? 300 : 'bold',
        }}
      >
        {label}
      </Typography>
      {!!value && <Typography sx={{ ml: 1 }}>{value}</Typography>}
      {action ? action : null}
      {explanationMsg ? (
        <Tooltip
          placement="top"
          title={<span style={{ fontSize: '1rem' }}>{explanationMsg}</span>}
        >
          <span
            style={{
              marginLeft: '8px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <HelpOutlineIcon fontSize="small" color="info" />
          </span>
        </Tooltip>
      ) : null}
    </Stack>
  );
};

const AreaTypeSettings = ({ data, test, segment }) => {
  const { openEditModal } = useTestContext();

  const { opAmount, spAmount, intervalCount } = data;

  const OPsection = () => {
    return (
      <TestCharacteristicItem
        icon={<CoronavirusIcon color="primary" />}
        label="Origin Points:"
        value={opAmount || ''}
        action={
          <IconButton
            aria-label="Edit number of origin points"
            size="small"
            sx={{ ml: 1 }}
            onClick={() => {
              openEditModal({
                type: 'origin_points',
                value: opAmount,
                testId: test.testid,
                data: {
                  preparedScenariosList: [],
                  test,
                  segment,
                },
              });
            }}
          >
            <EditIcon fontSize="inherit" />
          </IconButton>
        }
      />
    );
  };

  const SPsection = () => {
    return (
      <TestCharacteristicItem
        icon={<BiotechIcon color="primary" />}
        label="Sample Points:"
        value={spAmount || ''}
        action={
          <IconButton
            aria-label="Edit number of sample points"
            size="small"
            sx={{ ml: 1 }}
            onClick={() => {
              openEditModal({
                type: 'sample_points',
                value: spAmount,
                testId: test.testid,
                data: { test: test, segment },
              });
            }}
          >
            <EditIcon fontSize="inherit" />
          </IconButton>
        }
      />
    );
  };

  const intervalDurationSection = () => {
    return (
      <TestCharacteristicItem
        value={'UV'}
        label="Interval Duration:"
        explanationMsg="Interval duration depends on specific scenario settings"
      />
    );
  };

  const intervalCountSection = () => {
    return (
      <TestCharacteristicItem
        icon={<HourglassEmptyIcon color="primary" />}
        label="Interval Count:"
        value={intervalCount}
      />
    );
  };

  return (
    <Grid container mt={1} spacing={2} sx={{ overflow: 'hidden', pb: 2 }}>
      <Grid item xs={4}>
        {!!opAmount ? OPsection() : SPsection()}
      </Grid>
      <Grid item xs={4}>
        {!!opAmount ? SPsection() : null}
      </Grid>
      <Grid item xs={4} />
      <Grid item xs={4}>
        {intervalCountSection()}
      </Grid>
      <Grid item xs={4}>
        {intervalDurationSection()}
      </Grid>
      <Grid item xs={4}></Grid>
    </Grid>
  );
};

const UV = ({
  test,
  originPointsList,
  samplePointsList,
  surveydate,
  comments,
  preparedScenariosList,
  segmentId,
  segment,
  floorPlanPlacementURL,
  projectStatus,
}) => {
  const {
    projectId,
    openEditModal,
    reFetchProject,
    forceResrictAllEdits: forceResrictAllEditsFromContext,
    shouldUpdateScenariosData,
  } = useTestContext();

  const editSamplesDisabled =
    PROJECT_STATUS_WORKFLOW.indexOf(projectStatus) >=
    PROJECT_STATUS_WORKFLOW.indexOf(PROJECT_STATUSES.EXECUTION_READY);
  const editDetailsDisabled =
    PROJECT_STATUS_WORKFLOW.indexOf(projectStatus) >=
    PROJECT_STATUS_WORKFLOW.indexOf(PROJECT_STATUSES.EXECUTED);

  const forceResrictAllEdits =
    forceResrictAllEditsFromContext ||
    (typeof test?.testexecuted === 'boolean' && test?.testexecuted) ||
    editDetailsDisabled;
  const overrideEditPermissionSafetracesAdminIgroneWorkflow =
    isSafetracesAdmin();

  const testAreaSection = () => {
    const { testtype, testid: testId, buildingdata } = test;
    const { celling_height, sq_ft } = buildingdata || {};

    return (
      <Stack direction="column">
        <TestCharacteristicItem
          label="Test Area:"
          icon={<ViewInArIcon color="primary" />}
          action={
            overrideEditPermissionSafetracesAdminIgroneWorkflow ||
            (!forceResrictAllEdits && !editDetailsDisabled) ? (
              <IconButton
                aria-label="Edit test area"
                size="small"
                sx={{ ml: 1 }}
                onClick={() => {
                  openEditModal({
                    type: 'custom_area',
                    value: sq_ft,
                    testId,
                    data: {
                      sq_ft,
                      celling_height,
                      type: testtype,
                      segment,
                    },
                  });
                }}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            ) : null
          }
        />
        {!!sq_ft && (
          <TestCharacteristicItem
            variant="secondary"
            icon={<CropSquareIcon color="disabled" />}
            label="Square:"
            value={`${sq_ft} sq.ft`}
            sx={{ ml: 2, pt: 1 }}
          />
        )}
        {!!celling_height && (
          <TestCharacteristicItem
            icon={<HeightIcon color="disabled" />}
            label="Ceiling Height:"
            sx={{ ml: 2, pt: 1 }}
            value={`${celling_height} ft`}
            variant="secondary"
          />
        )}
      </Stack>
    );
  };

  return (
    <Stack direction="column" mt={2}>
      <Grid container spacing={2}>
        <Grid item xs={8} lg={8}>
          <Grid container spacing={2}>
            <Grid item xs={6} lg={6}>
              <TestCharacteristicItem
                label="Test Type:"
                value={TEST_TYPES_LABELS[test.testtype]}
              />
            </Grid>
            <Grid item xs={6} lg={6}>
              <TestCharacteristicItem
                icon={<CalendarMonthIcon color="primary" />}
                label="Survey Date:"
                value={
                  surveydate ? format(parseISO(surveydate), 'MMM do, yyyy') : ''
                }
                action={
                  !forceResrictAllEdits && !editDetailsDisabled ? (
                    <IconButton
                      aria-label="Edit survey date"
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() => {
                        openEditModal({
                          type: 'surveydate',
                          value: surveydate,
                          testId: test.testid,
                          data: { segment },
                        });
                      }}
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                  ) : null
                }
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <Stack>
                <TestCharacteristicItem
                  icon={<ImageOutlinedIcon color="primary" />}
                  label="Floorplan:"
                  action={
                    <Floorplan
                      projectId={projectId}
                      buildingId={test.buildingid || ''}
                      filteredTest={test}
                      segment={segment}
                      editDisabled={editDetailsDisabled || forceResrictAllEdits}
                    />
                  }
                />
                {segment.floorplan && (
                  <>
                    {!forceResrictAllEdits && !isSafetracesAdmin() ? (
                      <Typography sx={{ ml: 1, mt: 0.5 }}>
                        <Link href={floorPlanPlacementURL}>Manage OPs/SPs</Link>
                      </Typography>
                    ) : null}
                    {forceResrictAllEdits && !isSafetracesAdmin() ? (
                      <Typography sx={{ ml: 1, mt: 0.5 }}>
                        <Link href={floorPlanPlacementURL}>View OPs/SPs</Link>
                      </Typography>
                    ) : null}
                    {isSafetracesAdmin() &&
                    projectStatus !== PROJECT_STATUSES.PUBLISHED ? (
                      <Typography sx={{ ml: 1, mt: 0.5 }}>
                        <Link href={floorPlanPlacementURL}>Manage OPs/SPs</Link>
                      </Typography>
                    ) : null}
                    {isSafetracesAdmin() &&
                    projectStatus === PROJECT_STATUSES.PUBLISHED ? (
                      <Typography sx={{ ml: 1, mt: 0.5 }}>
                        <Link href={floorPlanPlacementURL}>View OPs/SPs</Link>
                      </Typography>
                    ) : null}{' '}
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4} lg={4}>
          {testAreaSection()}
        </Grid>
        <Grid item xs={12} md={12}>
          <TestCharacteristicItem
            label="Comments:"
            value={comments}
            action={
              overrideEditPermissionSafetracesAdminIgroneWorkflow ||
              !forceResrictAllEdits ? (
                <IconButton
                  aria-label="Edit comments"
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => {
                    openEditModal({
                      type: 'comments',
                      value: comments,
                      testId: test.testid,
                      data: { segmentId },
                    });
                  }}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
              ) : null
            }
          />
        </Grid>
      </Grid>
      <Grid container mt={0.5}>
        <Grid item xs={12} md={12}>
          <AreaTypeSettings
            data={test?.testmetadata?.[segment?.segmentid] || {}}
            opAmount={originPointsList?.length || 0}
            spAmount={samplePointsList?.length || 0}
            test={test}
            segment={segment}
          />
        </Grid>
      </Grid>
      <Stack direction="column" sx={{ mt: 2 }}>
        <TestScenariosContextProvider
          value={{
            projectId,
            testId: test.testid,
            testType: test.testtype,
            testExecuted: test.testexecuted,
            segmentId: test.segmentId,
            reFetchProject,
            projectStatus,
            shouldUpdateScenariosData,
          }}
        >
          <Stack direction="row" spacing={1} mb={1}>
            <Typography variant="h6">Scenarios</Typography>
            {!editSamplesDisabled ? <CreateScenarioButton /> : null}
          </Stack>
          <ScenariosTableSingleTest
            scenarios={preparedScenariosList}
            originPoints={originPointsList}
            test={test}
            editDisabled={forceResrictAllEdits}
          ></ScenariosTableSingleTest>
        </TestScenariosContextProvider>
      </Stack>
    </Stack>
  );
};

export default UV;
